import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { config } from "../config";
import { cn } from "../lib/utils";
import { useTheme } from "../context/ThemeContext";

// UI Components
import { Alert, AlertDescription } from "./ui/alert";
import { ScrollArea } from "./ui/scroll-area";
import {
  MandalaGridSkeleton,
  HymnGridSkeleton,
  VerseContentSkeleton,
} from "./ui/Skeleton";

// Layout Components
import { CommandPalette } from "./Layout/CommandPalette";
import { MantraPlayer } from "./Layout/MantraPlayer";
import { DynamicIsland } from "./Layout/DynamicIsland";

// Card Components
import { MandalaCard } from "./cards/MandalaCard";
import { DailyWisdom } from "./cards/DailyWisdom";

// Watermark
import NatarajaWatermark from "./NatarajaWatermark";

// Icons
import { Book, X, ChevronRight } from "lucide-react";

// Types
interface Mandala {
  number: number;
  total_hymns: number;
  description?: string;
}

interface Hymn {
  number: number;
  title?: string;
  total_verses?: number;
  deity?: string;
  rishi?: string;
}

interface Verse {
  number: number;
  sanskrit?: string;
  transliteration?: string;
  translation?: string;
}

interface HymnData {
  mandala: number;
  hymn_number: number;
  title?: string;
  deity?: string;
  rishi?: string;
  verses: Verse[];
}

type View = "mandalas" | "hymns" | "verse";

interface VedaExplorerNewProps {
  initialMandala?: number | null;
  onBack?: () => void;
}

export const VedaExplorerNew: React.FC<VedaExplorerNewProps> = ({
  initialMandala = null,
  onBack: onBackToLanding,
}) => {
  const [currentView, setCurrentView] = useState<View>(
    initialMandala ? "hymns" : "mandalas",
  );
  const [selectedMandala, setSelectedMandala] = useState<number | null>(
    initialMandala,
  );
  const [commandOpen, setCommandOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeVerseIndex, setActiveVerseIndex] = useState(0);

  // Data states
  const [mandalas, setMandalas] = useState<Mandala[]>([]);
  const [hymns, setHymns] = useState<Hymn[]>([]);
  const [currentHymnData, setCurrentHymnData] = useState<HymnData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { darkMode, toggleDarkMode } = useTheme();

  // Load mandalas on mount
  useEffect(() => {
    loadMandalas();
  }, []);

  // Load hymns if initialMandala is provided
  useEffect(() => {
    if (initialMandala) {
      loadHymns(initialMandala);
    }
  }, [initialMandala]);

  const loadMandalas = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/mandalas`);
      setMandalas(response.data);
    } catch (err) {
      setError("Failed to load mandalas. Please try again.");
      console.error("Error loading mandalas:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadHymns = async (mandalaNumber: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/mandalas/${mandalaNumber}/hymns`,
      );
      setHymns(response.data);
    } catch (err) {
      setError("Failed to load hymns. Please try again.");
      console.error("Error loading hymns:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadHymnData = async (mandalaNumber: number, hymnNumber: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/mandalas/${mandalaNumber}/hymns/${hymnNumber}`,
      );
      setCurrentHymnData(response.data);
      setActiveVerseIndex(0);
    } catch (err) {
      setError("Failed to load hymn data. Please try again.");
      console.error("Error loading hymn data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectMandala = useCallback((mandalaNumber: number) => {
    setSelectedMandala(mandalaNumber);
    loadHymns(mandalaNumber);
    setCurrentView("hymns");
    setSidebarOpen(false);
  }, []);

  const handleSelectHymn = useCallback(
    async (hymnNumber: number) => {
      if (selectedMandala) {
        await loadHymnData(selectedMandala, hymnNumber);
        setCurrentView("verse");
      }
    },
    [selectedMandala],
  );

  const handleBack = useCallback(() => {
    switch (currentView) {
      case "mandalas":
        // Go back to landing page
        if (onBackToLanding) {
          onBackToLanding();
        }
        break;
      case "hymns":
        setCurrentView("mandalas");
        setSelectedMandala(null);
        setHymns([]);
        break;
      case "verse":
        setCurrentView("hymns");
        setCurrentHymnData(null);
        break;
    }
  }, [currentView, onBackToLanding]);

  const handleRandomVerse = useCallback(async () => {
    const randomMandala = Math.floor(Math.random() * 10) + 1;
    setSelectedMandala(randomMandala);
    await loadHymns(randomMandala);
    setCurrentView("hymns");
  }, []);

  const activeVerse = currentHymnData?.verses?.[activeVerseIndex];

  return (
    <div
      className={cn(
        "min-h-screen flex flex-col relative",
        "bg-gallery-white dark:bg-gallery-black",
      )}
    >
      {/* Nataraja Watermark - Subtle Background */}
      <NatarajaWatermark />

      {/* Dynamic Island Header */}
      <DynamicIsland
        currentView={currentView}
        selectedMandala={selectedMandala}
        currentHymnData={currentHymnData}
        activeVerseNumber={activeVerse?.number}
        darkMode={darkMode}
        isLoading={isLoading}
        onToggleDarkMode={toggleDarkMode}
        onOpenSearch={() => setCommandOpen(true)}
        onRandomVerse={handleRandomVerse}
        onNavigateHome={() => {
          setCurrentView("mandalas");
          setSelectedMandala(null);
          setHymns([]);
          setCurrentHymnData(null);
        }}
        onNavigateBack={handleBack}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
      />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar - Museum Style */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 lg:hidden",
          "h-screen w-80",
          "bg-white dark:bg-museum-950",
          "border-r border-museum-200 dark:border-museum-800",
          "transform transition-transform duration-300 ease-museum-smooth",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between p-6 border-b border-museum-200 dark:border-museum-800">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-museum-400 dark:text-museum-500">
            Navigation
          </span>
          <button
            className="p-1 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <ScrollArea className="h-[calc(100vh-73px)]">
          <div className="p-4 space-y-1">
            {mandalas.map((mandala) => (
              <button
                key={mandala.number}
                onClick={() => handleSelectMandala(mandala.number)}
                className={cn(
                  "w-full flex items-center gap-4 px-4 py-3",
                  "text-left transition-colors duration-200",
                  "hover:bg-museum-50 dark:hover:bg-museum-900",
                  "rounded-museum",
                  selectedMandala === mandala.number &&
                    "bg-museum-100 dark:bg-museum-800",
                )}
              >
                <span className="font-display text-2xl font-light text-museum-300 dark:text-museum-600">
                  {mandala.number.toString().padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <p className="font-display text-base text-museum-800 dark:text-museum-200">
                    Mandala {mandala.number}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-museum-400 dark:text-museum-500">
                    {mandala.total_hymns} hymns
                  </p>
                </div>
                <ChevronRight
                  className="h-4 w-4 text-museum-300 dark:text-museum-600"
                  strokeWidth={1.5}
                />
              </button>
            ))}
          </div>
        </ScrollArea>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative z-10">
        <div className="max-w-6xl mx-auto px-6 py-12 lg:px-8 pb-32">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mb-8 rounded-museum">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Loading State - Contextual Skeletons */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Show appropriate skeleton based on what's loading */}
              {currentView === "mandalas" && (
                <div>
                  <header className="text-center mb-16">
                    <div className="h-4 w-32 bg-museum-100 dark:bg-museum-800 rounded mx-auto mb-4 animate-pulse" />
                    <div className="h-12 w-80 bg-museum-100 dark:bg-museum-800 rounded mx-auto mb-6 animate-pulse" />
                    <div className="h-6 w-96 bg-museum-100 dark:bg-museum-800 rounded mx-auto animate-pulse" />
                    <div className="w-16 h-px bg-museum-200 dark:bg-museum-700 mx-auto mt-8 mb-12" />
                  </header>
                  <MandalaGridSkeleton count={10} />
                </div>
              )}
              {currentView === "hymns" && (
                <div>
                  <div className="mb-12">
                    <div className="h-9 w-40 bg-museum-100 dark:bg-museum-800 rounded mb-2 animate-pulse" />
                    <div className="h-4 w-24 bg-museum-100 dark:bg-museum-800 rounded animate-pulse" />
                  </div>
                  <HymnGridSkeleton count={12} />
                </div>
              )}
              {currentView === "verse" && <VerseContentSkeleton />}
            </motion.div>
          )}

          {/* Page Content with Transitions */}
          <AnimatePresence mode="wait">
            {/* Mandalas Grid */}
            {currentView === "mandalas" && !isLoading && (
              <motion.div
                key="mandalas"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {/* Hero Section - Museum Typography */}
                <header className="text-center mb-16">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-museum-400 dark:text-museum-500 mb-4">
                    Rig Veda Collection
                  </p>

                  <h2
                    className={cn(
                      "font-display font-light",
                      "text-4xl sm:text-5xl lg:text-6xl",
                      "text-museum-900 dark:text-museum-100",
                      "tracking-tight leading-[1.1]",
                      "mb-6",
                    )}
                  >
                    The Ten Books
                    <br />
                    <span className="italic">of Wisdom</span>
                  </h2>

                  <p
                    className={cn(
                      "font-body",
                      "text-base sm:text-lg",
                      "text-museum-500 dark:text-museum-400",
                      "max-w-xl mx-auto leading-relaxed",
                    )}
                  >
                    Explore the ancient hymns of the Rig Veda, organized into
                    ten mandalas — the oldest sacred texts of humanity.
                  </p>

                  {/* Museum Divider */}
                  <div className="w-16 h-px bg-museum-200 dark:bg-museum-700 mx-auto mt-8 mb-12" />

                  {/* Daily Wisdom */}
                  <DailyWisdom className="max-w-2xl mx-auto mb-16" />
                </header>

                {/* Mandala Grid - Museum Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                  {mandalas.map((mandala, index) => (
                    <div
                      key={mandala.number}
                      className="animate-fade-in-up"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <MandalaCard
                        mandalaNumber={mandala.number}
                        totalHymns={mandala.total_hymns}
                        description={mandala.description}
                        onClick={() => handleSelectMandala(mandala.number)}
                        isSelected={selectedMandala === mandala.number}
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Hymns List - Museum Style */}
            {currentView === "hymns" && selectedMandala && !isLoading && (
              <motion.div
                key="hymns"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {/* Page Header */}
                <div className="mb-12">
                  <h2 className="font-display text-3xl font-normal text-museum-900 dark:text-museum-100">
                    Mandala {selectedMandala}
                  </h2>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-museum-400 dark:text-museum-500 mt-2">
                    {hymns.length} Hymns in this Book
                  </p>
                </div>

                {/* Hymns Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {hymns.map((hymn, index) => (
                    <article
                      key={hymn.number}
                      className={cn(
                        "group cursor-pointer",
                        "bg-white dark:bg-museum-950",
                        "border border-museum-200 dark:border-museum-800",
                        "rounded-museum p-6",
                        "transition-all duration-300 ease-museum",
                        "hover:border-museum-400 dark:hover:border-museum-600",
                        "hover:shadow-museum",
                        "animate-fade-in-up",
                      )}
                      style={{ animationDelay: `${index * 30}ms` }}
                      onClick={() => handleSelectHymn(hymn.number)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <span
                          className={cn(
                            "font-display font-light text-4xl",
                            "text-museum-200 dark:text-museum-800",
                          )}
                        >
                          {hymn.number.toString().padStart(2, "0")}
                        </span>
                        <Book
                          className="h-4 w-4 text-museum-300 dark:text-museum-600 group-hover:text-museum-500 dark:group-hover:text-museum-400 transition-colors"
                          strokeWidth={1.5}
                        />
                      </div>
                      <h3 className="font-display text-lg text-museum-900 dark:text-museum-100 mb-1">
                        Hymn {hymn.number}
                      </h3>
                      {hymn.title && (
                        <p className="font-body text-sm text-museum-500 dark:text-museum-400 line-clamp-1">
                          {hymn.title}
                        </p>
                      )}
                      {hymn.deity && (
                        <span
                          className={cn(
                            "inline-block mt-3",
                            "font-mono text-[9px] uppercase tracking-[0.15em]",
                            "text-museum-400 dark:text-museum-500",
                            "border border-museum-200 dark:border-museum-700",
                            "px-2 py-1 rounded-museum",
                          )}
                        >
                          {hymn.deity}
                        </span>
                      )}
                    </article>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Verse Display - All Verses */}
            {currentView === "verse" && currentHymnData && (
              <motion.div
                key="verse"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="max-w-4xl mx-auto"
              >
                {/* Hymn Header */}
                <header className="text-center mb-12">
                  <div className="flex items-center justify-center gap-4 mb-4 flex-wrap">
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-museum-400 dark:text-museum-500 px-2 py-1 border border-museum-200 dark:border-museum-700 rounded-museum">
                      Mandala {currentHymnData.mandala}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-museum-400 dark:text-museum-500 px-2 py-1 border border-museum-200 dark:border-museum-700 rounded-museum">
                      Hymn {currentHymnData.hymn_number}
                    </span>
                    {currentHymnData.deity && (
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-museum-400 dark:text-museum-500 px-2 py-1 border border-museum-200 dark:border-museum-700 rounded-museum">
                        {currentHymnData.deity}
                      </span>
                    )}
                  </div>
                  {currentHymnData.title && (
                    <h2 className="font-display text-3xl text-museum-900 dark:text-museum-100">
                      {currentHymnData.title}
                    </h2>
                  )}
                  {currentHymnData.rishi && (
                    <p className="font-body italic text-sm text-museum-500 dark:text-museum-400 mt-2">
                      Rishi: {currentHymnData.rishi}
                    </p>
                  )}
                  <div className="w-16 h-px bg-museum-200 dark:bg-museum-700 mx-auto mt-6" />
                </header>

                {/* All Verses */}
                <div className="space-y-10">
                  {currentHymnData.verses?.map((verse, index) => (
                    <article
                      key={verse.number}
                      className={cn(
                        "bg-white dark:bg-museum-950",
                        "border border-museum-200 dark:border-museum-800",
                        "rounded-museum",
                        "overflow-hidden",
                        "animate-fade-in-up",
                      )}
                      style={{ animationDelay: `${index * 60}ms` }}
                    >
                      {/* Verse number bar */}
                      <div className="flex items-center gap-3 px-6 py-3 border-b border-museum-100 dark:border-museum-800">
                        <span className="font-display text-2xl font-light text-museum-200 dark:text-museum-700">
                          {verse.number.toString().padStart(2, "0")}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-museum-400 dark:text-museum-500">
                          Verse {verse.number}
                        </span>
                      </div>

                      <div className="p-6 space-y-5">
                        {/* Sanskrit */}
                        {verse.sanskrit && (
                          <div>
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-museum-300 dark:text-museum-600 mb-2">
                              Sanskrit
                            </p>
                            <p className="font-sanskrit text-xl sm:text-2xl leading-loose text-museum-800 dark:text-museum-200">
                              {verse.sanskrit}
                            </p>
                          </div>
                        )}

                        {/* Transliteration */}
                        {verse.transliteration && (
                          <div>
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-museum-300 dark:text-museum-600 mb-2">
                              Transliteration
                            </p>
                            <p className="font-body italic text-base leading-relaxed text-museum-600 dark:text-museum-400">
                              {verse.transliteration}
                            </p>
                          </div>
                        )}

                        {/* Translation */}
                        {verse.translation && (
                          <div
                            className={cn(
                              "pt-4 border-t border-museum-100 dark:border-museum-800",
                            )}
                          >
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-museum-300 dark:text-museum-600 mb-2">
                              Translation
                            </p>
                            <p className="font-display text-lg leading-relaxed text-museum-700 dark:text-museum-300">
                              {verse.translation}
                            </p>
                          </div>
                        )}

                        {/* Empty state for verses with no content */}
                        {!verse.sanskrit &&
                          !verse.transliteration &&
                          !verse.translation && (
                            <p className="font-body italic text-sm text-museum-400 dark:text-museum-500 text-center py-4">
                              Text not available for this verse
                            </p>
                          )}
                      </div>
                    </article>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Command Palette */}
      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        onSelectMandala={handleSelectMandala}
        onSelectHymn={async (mandalaNum, hymnNum) => {
          setSelectedMandala(mandalaNum);
          await loadHymnData(mandalaNum, hymnNum);
          setCurrentView("verse");
        }}
        onRandomVerse={handleRandomVerse}
        onFeaturedVerse={handleRandomVerse}
      />

      {/* Floating Player */}
      {currentHymnData && currentHymnData.verses?.length > 0 && (
        <MantraPlayer
          currentVerse={{
            mandala: currentHymnData.mandala,
            hymn: currentHymnData.hymn_number,
            verse: 1,
            sanskrit: currentHymnData.verses[0]?.sanskrit,
            transliteration: currentHymnData.verses[0]?.transliteration,
            translation: currentHymnData.verses[0]?.translation,
          }}
          isPlaying={isPlaying}
          onPlayPause={() => setIsPlaying(!isPlaying)}
          onOpenFull={() => setCurrentView("verse")}
        />
      )}

      {/* Footer - Museum Minimal */}
      <footer
        className={cn(
          "fixed bottom-0 left-0 right-0 z-10",
          "bg-white/95 dark:bg-museum-950/95",
          "backdrop-blur-sm",
          "border-t border-museum-200 dark:border-museum-800",
        )}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <p className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.15em] text-museum-400 dark:text-museum-500">
              Sacred Archive
            </p>
            <p className="font-display text-sm text-museum-600 dark:text-museum-400 mx-auto sm:mx-0">
              Veda Futura
            </p>
            <p className="hidden lg:block font-sanskrit text-sm text-museum-400 dark:text-museum-500">
              ऋग्वेद
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VedaExplorerNew;
